import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BarChart3, Brain, RefreshCw, TrendingUp, Users, Activity, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function AdminPage() {
  const { user } = useAuth();
  const [latestReport, setLatestReport] = useState<any>(null);
  const [evaluation, setEvaluation] = useState<any>(null);
  const [recentLogs, setRecentLogs] = useState<any[]>([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingEval, setLoadingEval] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load latest report
      const { data: reports } = await (supabase as any)
        .from("weekly_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1);
      if (reports?.[0]) setLatestReport(reports[0]);

      // Load recent activity logs
      const { data: logs } = await (supabase as any)
        .from("user_activity_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      setRecentLogs(logs || []);
    } catch (e) {
      console.warn("Failed to load admin data:", e);
    }
    setLoading(false);
  };

  const runEval = async () => {
    setLoadingEval(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-app-eval");
      if (error) throw error;
      setEvaluation(data);
      toast.success("AI Eval complete ✨");
    } catch (e: any) {
      toast.error("Eval failed", { description: e.message });
    }
    setLoadingEval(false);
  };

  const generateReport = async () => {
    setLoadingReport(true);
    try {
      const { data, error } = await supabase.functions.invoke("weekly-usage-report");
      if (error) throw error;
      if (data?.report) {
        setLatestReport({ report_data: data.report, created_at: new Date().toISOString() });
      }
      toast.success("Report generated ✨");
    } catch (e: any) {
      toast.error("Report failed", { description: e.message });
    }
    setLoadingReport(false);
  };

  const report = latestReport?.report_data;
  const evalData = evaluation?.evaluation;

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Admin Dashboard</h1>
        <p className="mt-2 text-muted-foreground text-lg">Analytics, AI evaluation, and activity monitoring.</p>
      </motion.div>

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={runEval}
          disabled={loadingEval}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Brain className="w-4 h-4" />
          {loadingEval ? "Running Eval..." : "Run AI Eval"}
        </button>
        <button
          onClick={generateReport}
          disabled={loadingReport}
          className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80 disabled:opacity-50"
        >
          <BarChart3 className="w-4 h-4" />
          {loadingReport ? "Generating..." : "Generate Report Now"}
        </button>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2.5 bg-secondary text-secondary-foreground rounded-lg text-sm font-medium hover:bg-secondary/80"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* AI Eval Results */}
      {evalData && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
          <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" /> AI Evaluation
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-sage-light rounded-xl p-4 text-center">
              <p className="text-3xl font-display font-bold text-foreground">{evalData.usability_score}</p>
              <p className="text-sm text-muted-foreground mt-1">Usability</p>
              <p className="text-xs text-muted-foreground mt-2">{evalData.usability_reasoning}</p>
            </div>
            <div className="bg-terracotta-light rounded-xl p-4 text-center">
              <p className="text-3xl font-display font-bold text-foreground">{evalData.stickiness_score}</p>
              <p className="text-sm text-muted-foreground mt-1">Stickiness</p>
              <p className="text-xs text-muted-foreground mt-2">{evalData.stickiness_reasoning}</p>
            </div>
          </div>
          {evalData.suggestions?.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Improvement Suggestions</h3>
              {evalData.suggestions.map((s: any, i: number) => (
                <div key={i} className="bg-background rounded-lg p-3 flex items-start gap-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    s.impact === "high" ? "bg-accent/20 text-accent" :
                    s.impact === "medium" ? "bg-primary/20 text-primary" :
                    "bg-muted text-muted-foreground"
                  }`}>{s.impact}</span>
                  <div>
                    <p className="text-sm font-medium text-foreground">{s.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Weekly Report */}
      {report && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Weekly Report
            </h2>
            {latestReport?.created_at && (
              <span className="text-xs text-muted-foreground">
                {new Date(latestReport.created_at).toLocaleDateString()}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard icon={<Zap className="w-4 h-4" />} label="North Star" value={report.northStar?.couplesWithWeeklyEngagement ?? 0} sub="Couples engaged" />
            <MetricCard icon={<Users className="w-4 h-4" />} label="Active Users" value={report.activeUsers ?? 0} sub="Past 7 days" />
            <MetricCard icon={<Activity className="w-4 h-4" />} label="Stickiness" value={report.stickiness ?? "0%"} sub="DAU/WAU" />
            <MetricCard icon={<Users className="w-4 h-4" />} label="New Signups" value={report.newSignups ?? 0} sub="This week" />
          </div>

          {report.eventBreakdown && Object.keys(report.eventBreakdown).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Event Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(report.eventBreakdown).map(([event, count]) => (
                  <div key={event} className="bg-background rounded-lg p-2 flex justify-between text-sm">
                    <span className="text-muted-foreground">{event.replace(/_/g, " ")}</span>
                    <span className="font-medium text-foreground">{String(count)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.topUsers?.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground">Top Users</h3>
              {report.topUsers.map((u: any, i: number) => (
                <div key={i} className="bg-background rounded-lg p-2 flex justify-between text-sm">
                  <span className="text-foreground">{u.name} <span className="text-muted-foreground">({u.email})</span></span>
                  <span className="font-medium text-foreground">{u.events} events</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-4 text-sm">
            <span className="text-muted-foreground">Retention: <strong className="text-foreground">{report.retention}</strong></span>
            <span className="text-muted-foreground">Returning: <strong className="text-foreground">{report.returningUsers}</strong></span>
          </div>
        </motion.div>
      )}

      {/* Activity Stream */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-xl p-6 space-y-3">
        <h2 className="font-display text-xl font-semibold text-foreground flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" /> Recent Activity
        </h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : recentLogs.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No activity logs yet.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto space-y-1">
            {recentLogs.map((log) => (
              <div key={log.id} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0">
                <span className="text-foreground">{log.event_type.replace(/_/g, " ")}</span>
                <span className="text-muted-foreground text-xs">
                  {new Date(log.created_at).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function MetricCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub: string }) {
  return (
    <div className="bg-background rounded-xl p-3 text-center">
      <div className="flex items-center justify-center gap-1.5 text-muted-foreground mb-1">{icon}<span className="text-xs">{label}</span></div>
      <p className="text-2xl font-display font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{sub}</p>
    </div>
  );
}
